<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue'

// New-design custom date picker (.datepicker-pop) — replaces v-calendar
// in forms. value — an ISO string 'YYYY-MM-DD'.
const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Select date…' },
})
const emit = defineEmits(['update:modelValue'])

const open = ref(false)
const rootRef = ref(null)

const selected = computed(() => (props.modelValue ? new Date(props.modelValue + 'T00:00:00') : null))
const viewDate = ref(selected.value || new Date())

function onDocClick(e) {
  if (open.value && rootRef.value && !rootRef.value.contains(e.target)) open.value = false
}
onMounted(() => document.addEventListener('mousedown', onDocClick))
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick))

function toggle() {
  viewDate.value = selected.value || new Date()
  open.value = !open.value
}

function addDays(date, n) { const d = new Date(date); d.setDate(d.getDate() + n); return d }
function addMonths(n) { const d = new Date(viewDate.value); d.setMonth(d.getMonth() + n); viewDate.value = d }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
const pad2 = (n) => String(n).padStart(2, '0')
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

function pick(d) {
  emit('update:modelValue', toISO(d))
  open.value = false
}

const today = computed(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t })

const monthLabel = computed(() =>
  new Date(viewDate.value.getFullYear(), viewDate.value.getMonth(), 1)
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
)

const cells = computed(() => {
  const y = viewDate.value.getFullYear()
  const m = viewDate.value.getMonth()
  const first = new Date(y, m, 1)
  const startOffset = (first.getDay() + 6) % 7 // grid starts on Monday
  const gridStart = addDays(first, -startOffset)
  return Array.from({ length: 42 }, (_, i) => {
    const d = addDays(gridStart, i)
    return {
      date: d,
      muted: d.getMonth() !== m,
      isToday: sameDay(d, today.value),
      isSelected: selected.value ? sameDay(d, selected.value) : false,
    }
  })
})

const display = computed(() =>
  selected.value
    ? selected.value.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : props.placeholder
)

const DOW = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
</script>

<template>
  <div ref="rootRef" class="field-date">
    <button type="button" class="field__input field-date__trigger" @click="toggle">
      <span class="mdi mdi-calendar-blank-outline"></span>
      <span :class="{ 'field-date__placeholder': !selected }">{{ display }}</span>
    </button>
    <div v-if="open" class="datepicker-pop">
      <div class="datepicker-pop__head">
        <button type="button" class="datepicker-pop__nav" @click="addMonths(-1)">
          <span class="mdi mdi-chevron-left"></span>
        </button>
        <div class="datepicker-pop__label">{{ monthLabel }}</div>
        <button type="button" class="datepicker-pop__nav" @click="addMonths(1)">
          <span class="mdi mdi-chevron-right"></span>
        </button>
      </div>
      <div class="datepicker-pop__grid">
        <span v-for="d in DOW" :key="d" class="datepicker-pop__dow">{{ d }}</span>
        <button
          v-for="(cell, i) in cells"
          :key="i"
          type="button"
          class="datepicker-pop__day"
          :class="{ 'is-muted': cell.muted, 'is-today': cell.isToday, 'is-selected': cell.isSelected }"
          @click="pick(cell.date)">
          {{ cell.date.getDate() }}
        </button>
      </div>
      <button type="button" class="datepicker-pop__today" @click="pick(today)">Today</button>
    </div>
  </div>
</template>
